// src/oura/components/plan/PlanLineSheet.tsx
//
// One plan line in the Contract tab's drawer (plan table spec v1.1, 7): what
// the client sees, the months it runs, the quantity, and how it is confirmed.
// Edits go into the plan's draft as they are typed; nothing is sent until
// the plan is saved from the bar under the page. Done checks the fields and
// closes; X and Escape always close, and a field left wrong stays marked on
// its row. "Remove line" asks first.
//
// "Add a line" opens the same sheet on a new line that lives here until
// "Add line": a custom X code, confirmed by hand, with no scan check.
//
// Keyed per line by the host, so the fields shown, the confirm and the
// errors reset on a move.

import { useId, useRef, useState } from "react";
import { cn } from "../../lib/cn";
import {
    DrawerBlock,
    DrawerFold,
    DrawerFoot,
    DrawerHead,
    DrawerNotice,
    FamilyChip,
    LATE_TEXT,
    PillButton,
    TYPE,
    Tag,
    useDrawerFocusClaim,
    type DrawerStatus,
} from "@oura/ui";
import { CoversIcon, CoversMark } from "./CoversMark";
import { monthEndDay } from "./planDates";
import {
    BY_HAND_WORDS,
    COVERED_WORDS,
    checksText,
    familyOf,
    formatMonths,
    isCovered,
    isEdited,
    labelError,
    MONTHS_EXAMPLE,
    monthlyUnits,
    monthsError,
    parseMonths,
    placementMonths,
    rowTiming,
    type DraftLine,
    type PlanChecks,
} from "./planModel";

const FIELD = cn(
    TYPE.tb,
    "block w-full rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-rose-300",
);

export type LinePatch = Partial<Pick<DraftLine, "label" | "monthsText" | "quantityText">>;

export interface PlanLineSheetProps {
    mode: "edit" | "add";
    line: DraftLine;
    term: number;
    /** The term month now (monthOf on the draft's start). */
    month: number;
    notStarted: boolean;
    /** The start date as it stands in the draft. */
    start: string;
    /** The title of the time group the line sits in now. */
    groupTitle: string | null;
    checks?: PlanChecks;
    /** Pages the contract sells, for G1's sum. */
    pageUnits: number;
    /** The server's words about this line from the last refused save. */
    refusal: string[] | null;
    /** Show every field's problem at once: a save was tried, or the row already says "to fix". */
    revealErrors: boolean;
    busy: boolean;
    onChange: (patch: LinePatch) => void;
    /** Edit: the fields read; close. */
    onDone: () => void;
    /** Add: the fields read; put the line in the plan. */
    onAdd: () => void;
    /** Add: drop the new line. */
    onCancel: () => void;
    /** Edit: confirmed; take the line out of the plan. */
    onRemove: () => void;
}

export function PlanLineSheet({
    mode,
    line,
    term,
    month,
    notStarted,
    start,
    groupTitle,
    checks,
    pageUnits,
    refusal,
    revealErrors,
    busy,
    onChange,
    onDone,
    onAdd,
    onCancel,
    onRemove,
}: PlanLineSheetProps) {
    const ids = { label: useId(), months: useId(), quantity: useId(), labelErr: useId(), monthsErr: useId(), monthsHelp: useId(), qtyHelp: useId() };
    const labelRef = useRef<HTMLInputElement | null>(null);
    const monthsRef = useRef<HTMLInputElement>(null);
    const claim = useDrawerFocusClaim();
    const [shown, setShown] = useState({ label: revealErrors, months: revealErrors });
    const [confirming, setConfirming] = useState(false);

    const labelErr = labelError(line);
    const monthsErr = monthsError(line, term);
    const covered = isCovered(line);
    const label = line.label.trim();
    const parsed = parseMonths(line.monthsText, term);
    const months = placementMonths(line, term);
    const differs = mode === "edit" && (!line.saved || isEdited(line, term));

    // The header's status: the group it sits in, and where it falls in time.
    let status: DrawerStatus | null = null;
    if (mode === "edit" || parsed.ok) {
        const timing = rowTiming(months, month, notStarted);
        if (timing.kind === "none") status = { tone: "red", label: "No month set" };
        else {
            // A closed window says "Due by", never "Ended": the plan has no
            // done state, so a line past its month may simply have slipped.
            const clock =
                timing.kind === "now"
                    ? "This month"
                    : timing.kind === "not_started"
                        ? "Not started"
                        : timing.kind === "next"
                            ? `Next: month ${timing.month}`
                            : monthEndDay(start, timing.month)
                                ? `Due by ${monthEndDay(start, timing.month)}`
                                : `Due in month ${timing.month}`;
            status = { tone: timing.kind === "now" ? "blue" : "gray", label: groupTitle ?? "The plan", clock: { text: clock } };
        }
    }

    /** "2,3,4,5" reads as "2-5" once the field is left. */
    const normaliseMonths = () => {
        if (parsed.ok && formatMonths(parsed.months) !== line.monthsText) onChange({ monthsText: formatMonths(parsed.months) });
    };

    const primary = () => {
        if (busy) return;
        if (labelErr || monthsErr) {
            setShown({ label: true, months: true });
            (labelErr ? labelRef.current : monthsRef.current)?.focus();
            return;
        }
        normaliseMonths();
        if (mode === "add") onAdd();
        else onDone();
    };

    const showLabelErr = shown.label && labelErr;
    const showMonthsErr = shown.months && monthsErr;

    // G1 is the line the server counts pages from: its leading number times its months.
    const isG1 = line.code.trim().toUpperCase() === "G1";
    const perMonth = monthlyUnits(line.quantityText);
    const pages = (perMonth ?? 0) * months.length;
    const pageSum = !isG1
        ? null
        : perMonth === null
            ? `No number in the quantity, so 0 pages count. The contract sells ${pageUnits}.`
            : `${perMonth} a month × ${months.length} ${months.length === 1 ? "month" : "months"} = ${pages} pages. The contract sells ${pageUnits}.`;

    return (
        <>
            <DrawerHead
                lead={<CoversMark line={line} />}
                leadKind="icon"
                title={label || (mode === "add" ? "New line" : "No label yet")}
                chips={<FamilyChip family={familyOf(line.code)}>{line.code}</FamilyChip>}
                status={status}
            />
            <form
                noValidate
                className="space-y-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    primary();
                }}
            >
                {refusal && refusal.length > 0 && (
                    <DrawerNotice>
                        <p className="m-0 font-medium">Refused on save</p>
                        {refusal.map((m, i) => (
                            <p key={i} className="m-0 mt-1 break-words">
                                {m}
                            </p>
                        ))}
                    </DrawerNotice>
                )}

                <DrawerBlock label={<label htmlFor={ids.label}>What the client sees</label>}>
                    <input
                        ref={(el) => {
                            labelRef.current = el;
                            if (mode === "add") return claim(el);
                        }}
                        id={ids.label}
                        type="text"
                        maxLength={120}
                        autoComplete="off"
                        value={line.label}
                        aria-invalid={showLabelErr ? true : undefined}
                        aria-describedby={showLabelErr ? ids.labelErr : undefined}
                        onChange={(e) => onChange({ label: e.target.value })}
                        onBlur={() => setShown((s) => ({ ...s, label: true }))}
                        className={cn(FIELD, "mt-1")}
                    />
                    {showLabelErr && (
                        <p id={ids.labelErr} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-1.5")}>
                            {labelErr}
                        </p>
                    )}
                </DrawerBlock>

                <DrawerBlock label={<label htmlFor={ids.months}>Months</label>}>
                    <input
                        ref={monthsRef}
                        id={ids.months}
                        // No inputMode: iOS's numeric pad has no "-" or ",",
                        // so "2-12" and "3, 6, 9" could not be typed on a phone.
                        type="text"
                        autoComplete="off"
                        value={line.monthsText}
                        aria-invalid={showMonthsErr ? true : undefined}
                        aria-describedby={[showMonthsErr && ids.monthsErr, ids.monthsHelp].filter(Boolean).join(" ")}
                        onChange={(e) => onChange({ monthsText: e.target.value })}
                        onBlur={() => {
                            setShown((s) => ({ ...s, months: true }));
                            normaliseMonths();
                        }}
                        className={cn(FIELD, "mt-1 tabular-nums")}
                    />
                    {showMonthsErr && (
                        <p id={ids.monthsErr} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-1.5")}>
                            {monthsErr}
                        </p>
                    )}
                    <div id={ids.monthsHelp}>
                        <p className={cn(TYPE.tc, "m-0 mt-1.5 tabular-nums text-gray-500")}>
                            {MONTHS_EXAMPLE}
                            {!notStarted && ` · Now month ${month} of ${term}`}
                        </p>
                        {pageSum && (
                            <p className={cn(TYPE.tc, "m-0 mt-1 tabular-nums", pages !== pageUnits ? "text-rose-600" : "text-gray-500")}>{pageSum}</p>
                        )}
                    </div>
                </DrawerBlock>

                <DrawerBlock label={<label htmlFor={ids.quantity}>Quantity</label>}>
                    <input
                        id={ids.quantity}
                        type="text"
                        autoComplete="off"
                        value={line.quantityText}
                        aria-describedby={ids.qtyHelp}
                        onChange={(e) => onChange({ quantityText: e.target.value })}
                        className={cn(FIELD, "mt-1")}
                    />
                    <p id={ids.qtyHelp} className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                        What the client gets, like 10 pages or 3 a month. Leave empty for none.
                    </p>
                </DrawerBlock>

                <DrawerBlock label="How it's confirmed">
                    <Confirmed line={line} covered={covered} checks={checks} isNew={mode === "add"} custom={familyOf(line.code) === "x"} />
                </DrawerBlock>

                {(line.cadence || line.note) && (
                    <DrawerFold id="staff-plan.from-quote" label="From the quotation" note={line.cadence ?? undefined} remember>
                        {line.cadence && <p className="m-0">Cadence: {line.cadence}</p>}
                        {line.note && <p className={cn("m-0", line.cadence && "mt-1")}>Note: {line.note}</p>}
                    </DrawerFold>
                )}
                {/* The visible Done sits in the foot, outside this form. With
                    three fields and no submit button of its own, Enter would
                    submit nothing; this one makes Enter run it. */}
                <button type="submit" tabIndex={-1} aria-hidden className="sr-only" />
            </form>

            <DrawerFoot
                message={differs && !confirming ? { tone: "info", text: "Kept here until you save the plan." } : null}
                first={(
                    <PillButton variant="primary" disabled={busy} onClick={primary}>
                        {mode === "add" ? "Add line" : "Done"}
                    </PillButton>
                )}
                second={
                    mode === "add" ? (
                        <PillButton disabled={busy} onClick={onCancel}>
                            Cancel
                        </PillButton>
                    ) : (
                        <PillButton disabled={busy} onClick={() => setConfirming(true)}>
                            Remove line
                        </PillButton>
                    )
                }
                confirm={
                    confirming
                        ? {
                            text: `Remove “${label || line.code}” from the plan? The client stops seeing it once you save.`,
                            verb: "Remove line",
                            onConfirm: onRemove,
                            onCancel: () => setConfirming(false),
                        }
                        : null
                }
            />
        </>
    );
}

/** How the line is proven: the scan's checks, read-only, or the team's word. */
function Confirmed({ line, covered, checks, isNew, custom }: { line: DraftLine; covered: boolean; checks?: PlanChecks; isNew: boolean; custom: boolean }) {
    if (isNew) {
        return (
            <p className="m-0 flex items-center gap-2">
                <CoversIcon covered={false} />
                {BY_HAND_WORDS}. A new line has no scan check.
            </p>
        );
    }
    return (
        <>
            <p className="m-0 flex items-center gap-2 font-medium">
                <CoversIcon covered={covered} />
                {covered ? `${COVERED_WORDS} · ${checksText(line.covers.length)}` : BY_HAND_WORDS}
            </p>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>
                {covered
                    ? "Each site scan reads these checks, and the client sees the result on this line. It counts in their done count."
                    : "No site scan can see this work, so the team confirms it. It stays out of the client's done count unless a task tracks it."}
            </p>
            {covered && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {line.covers.map((key) => {
                        const c = checks?.[key];
                        const name = c?.label?.trim() || key.replace(/_/g, " ");
                        // Absent is "not measured yet", never a measured zero.
                        if (!c || typeof c.count !== "number") return <Tag key={key} tone="gray">{name} · not measured yet</Tag>;
                        return c.count === 0 ? (
                            <Tag key={key} tone="green">{name} · clear</Tag>
                        ) : (
                            <Tag key={key} tone="rose">
                                {name} · {c.count} {c.count === 1 ? "page" : "pages"}
                            </Tag>
                        );
                    })}
                </div>
            )}
            {/* A custom X line is not in the catalogue: its list was set when it was added. */}
            {!custom && <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Set by the catalogue.</p>}
        </>
    );
}
